import React, { useState, useMemo } from 'react'
import {
  Box,
  Typography,
  Paper,
  Grid,
  TextField,
  Button,
  FormControlLabel,
  Checkbox,
  Radio,
  RadioGroup,
  FormControl,
  FormLabel,
  Select,
  MenuItem,
  InputLabel,
  Divider,
  Alert,
  Tooltip,
  Chip,
} from '@mui/material'
import {
  ShoppingCart as CartIcon,
  LocalShipping as ShippingIcon,
  CreditCard as StripeIcon,
  Bookmark as BookmarkIcon,
  InfoOutlined as InfoIcon,
  Person as PersonIcon,
} from '@mui/icons-material'
import { useNavigate } from 'react-router-dom'
import CustomerLayout from '@/components/Layout/CustomerLayout'
import { useCustomerAuth } from '@/context/CustomerAuthContext'
import { mockProducts, mockOrders } from '@/data/mockData'
import { calculateShippingFee, extractProductWeightKg } from '@/utils/shippingRates'
import { searchAddressByPostalCode } from '@/utils/postalCode'
import type { Order, Product } from '@/types'

export const CheckoutPage: React.FC = () => {
  const navigate = useNavigate()
  const { user, isLoggedIn, savedAddresses, addSavedAddress } = useCustomerAuth()

  // 1. 商品選択状態
  const [selectedProductId, setSelectedProductId] = useState<number>(mockProducts[0].ProductId)
  const [quantity, setQuantity] = useState<number>(1)

  // 2. お届け先モード
  // isSendToSelf: true (自分に送る - デフォルト) / false (異なる住所に送る)
  const [isSendToSelf, setIsSendToSelf] = useState<boolean>(true)
  const [differentAddressMode, setDifferentAddressMode] = useState<'saved' | 'new'>('new')
  const [selectedSavedConsigneeId, setSelectedSavedConsigneeId] = useState<number>(
    savedAddresses.length > 0 ? savedAddresses[0].UserConsigneeId : 0
  )

  // 3. 依頼主（Shipper）フォーム情報（ログインユーザーの情報を既定設定）
  const [shipperForm, setShipperForm] = useState({
    Name: user?.Name || '山田 太郎',
    Furigana: user?.Furigana || 'ヤマダ タロウ',
    Phone: user?.Phone || '090-1234-5678',
    PostalCD: user?.Address?.PostalCD || '1500043',
    PrefectureName: user?.Address?.PrefectureName || '東京都',
    CityName: user?.Address?.CityName || '渋谷区',
    Address1: user?.Address?.Address1 || '道玄坂1-2-3',
    Address2: user?.Address?.Address2 || '渋谷ビル 4F',
  })

  // 4. 異なるお届け先（Consignee）新規入力フォーム情報
  const [consigneeForm, setConsigneeForm] = useState({
    Name: '鈴木 花子',
    Furigana: 'スズキ ハナコ',
    Phone: '080-9876-5432',
    PostalCD: '5410041',
    PrefectureName: '大阪府',
    CityName: '大阪市中央区',
    Address1: '北浜1-2-3',
    Address2: 'パークマンション101',
    SaveToAddressBook: true,
  })

  // 5. 規約同意チェック
  const [agreedTerms, setAgreedTerms] = useState<boolean>(false)
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false)
  const [searchLoading, setSearchLoading] = useState<boolean>(false)
  const [postalNotice, setPostalNotice] = useState<string>('')

  // 選択された商品情報
  const selectedProduct = useMemo<Product>(() => {
    return mockProducts.find((p) => p.ProductId === selectedProductId) || mockProducts[0]
  }, [selectedProductId])

  // お届け先の都道府県の決定
  const targetPrefecture = useMemo(() => {
    if (isSendToSelf) {
      return shipperForm.PrefectureName
    }
    if (differentAddressMode === 'saved') {
      const saved = savedAddresses.find((a) => a.UserConsigneeId === selectedSavedConsigneeId)
      return saved?.Consignee?.Address?.PrefectureName || '東京都'
    }
    return consigneeForm.PrefectureName
  }, [isSendToSelf, shipperForm.PrefectureName, differentAddressMode, selectedSavedConsigneeId, savedAddresses, consigneeForm.PrefectureName])

  // 料金計算
  const unitPrice = selectedProduct.UnitPrice
  const itemSubtotal = unitPrice * quantity
  const totalWeightKg = extractProductWeightKg(selectedProduct.ProductName) * quantity
  const shippingFee = calculateShippingFee(targetPrefecture, totalWeightKg)
  const taxAmount = Math.floor(itemSubtotal * 0.1) // 10%
  const grandTotal = itemSubtotal + taxAmount + shippingFee

  // 郵便番号から住所検索（Zipcloud API 連携）
  const handleZipSearch = async (postalCD: string, isConsignee: boolean) => {
    setSearchLoading(true)
    setPostalNotice('')

    const result = await searchAddressByPostalCode(postalCD)
    setSearchLoading(false)

    if (result) {
      const updateData = {
        PrefectureName: result.prefecture,
        CityName: result.city,
        Address1: result.town,
      }
      if (isConsignee) {
        setConsigneeForm((prev) => ({ ...prev, ...updateData }))
      } else {
        setShipperForm((prev) => ({ ...prev, ...updateData }))
      }
      setPostalNotice(`郵便番号から住所を自動補完しました（${result.prefecture}${result.city}${result.town}）`)
    } else {
      setPostalNotice('該当する住所が見つかりませんでした。7桁の郵便番号をご確認ください。')
    }
  }

  // 注文決定ボタン押下（Stripe Checkoutへ遷移）
  const handleSubmitOrder = () => {
    if (!agreedTerms) return
    setIsSubmitting(true)

    // 新規配送先のアドレス帳保存
    if (!isSendToSelf && differentAddressMode === 'new' && consigneeForm.SaveToAddressBook) {
      addSavedAddress(
        {
          Name: consigneeForm.Name,
          Furigana: consigneeForm.Furigana,
          Phone: consigneeForm.Phone,
          PostalCD: consigneeForm.PostalCD,
          PrefectureName: consigneeForm.PrefectureName,
          CityName: consigneeForm.CityName,
          Address1: consigneeForm.Address1,
          Address2: consigneeForm.Address2,
        },
        `${consigneeForm.Name}様宛`
      )
    }

    // 注文レコードを生成し追加
    const newOrderId = Date.now()
    const newOrder: Order = {
      OrderId: newOrderId,
      OrderDate: new Date().toISOString().split('T')[0],
      ShipperId: user?.ShipperId || 1,
      StoreId: 1, // デフォルト店舗自動割り当て
      UserId: user?.UserId || 1,
      ShippingFee: shippingFee,
      OrderTotal: grandTotal,
      ItemCount: quantity,
      StripePaymentIntentId: `pi_mock_${Date.now()}`,
      StripePaymentSessionId: `cs_test_${Date.now()}`,
      PaymentStatus: 'authorized', // 購入時＝与信確保（仮売上）
      OrderStatus: 'ordered',
      CreatedAt: new Date().toISOString(),
      UpdatedAt: new Date().toISOString(),
      ShipperName: shipperForm.Name,
      StoreName: 'ヤマト運輸 東京主管支店',
      OrderDetails: [
        {
          OrderDetailId: Date.now() + 1,
          OrderId: newOrderId,
          ConsigneeId: 1,
          ProductId: selectedProduct.ProductId,
          Quantity: quantity,
          UnitPrice: selectedProduct.UnitPrice,
          LineTotal: grandTotal,
          CreatedAt: new Date().toISOString(),
          UpdatedAt: new Date().toISOString(),
          ConsigneeName: isSendToSelf
            ? shipperForm.Name
            : differentAddressMode === 'saved'
            ? savedAddresses.find((a) => a.UserConsigneeId === selectedSavedConsigneeId)?.Consignee?.Address?.Name || 'お届け先様'
            : consigneeForm.Name,
          ProductName: selectedProduct.ProductName,
        },
      ],
    }

    // モック注文データ配列に追加
    mockOrders.unshift(newOrder)

    // Stripe Checkout リダイレクトのシミュレーション
    setTimeout(() => {
      setIsSubmitting(false)
      navigate(`/checkout/complete?orderId=${newOrderId}`)
    }, 1000)
  }

  return (
    <CustomerLayout>
      <Box sx={{ maxW: 1100, mx: 'auto' }}>
        <Typography variant="h4" fontWeight={700} gutterBottom sx={{ color: '#0f172a' }}>
          ご注文入力・お手続き
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
          商品・お届け先の選択およびカード与信手続きを行ないます。
        </Typography>

        {!isLoggedIn && (
          <Alert severity="info" sx={{ mb: 3 }} action={<Button color="inherit" size="small" onClick={() => navigate('/login')}>ログインする</Button>}>
            現在ゲストモードです。Googleログインを行うと既定住所の自動入力機能をご利用いただけます。
          </Alert>
        )}

        <Grid container spacing={3}>
          {/* 左カラム: 入力フォームエリア */}
          <Grid item xs={12} md={7}>
            {/* STEP 1: 商品選択 */}
            <Paper elevation={0} sx={{ p: 3, mb: 3, borderRadius: 3, border: '1px solid #e2e8f0' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <CartIcon color="primary" />
                <Typography variant="h6" fontWeight={700}>
                  1. ご注文商品の選択
                </Typography>
              </Box>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={8}>
                  <FormControl fullWidth size="small">
                    <InputLabel>対象商品</InputLabel>
                    <Select
                      value={selectedProductId}
                      label="対象商品"
                      onChange={(e) => setSelectedProductId(Number(e.target.value))}
                    >
                      {mockProducts.map((prod) => (
                        <MenuItem key={prod.ProductId} value={prod.ProductId}>
                          {prod.ProductName} — ¥{prod.UnitPrice.toLocaleString()} (税込)
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    size="small"
                    type="number"
                    label="数量"
                    inputProps={{ min: 1, max: 50 }}
                    value={quantity}
                    onChange={(e) => setQuantity(Math.max(1, Number(e.target.value)))}
                  />
                </Grid>
              </Grid>
            </Paper>

            {/* STEP 2: 依頼主情報（自分） */}
            <Paper elevation={0} sx={{ p: 3, mb: 3, borderRadius: 3, border: '1px solid #e2e8f0' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <PersonIcon color="primary" />
                  <Typography variant="h6" fontWeight={700}>
                    2. ご依頼主（ご注文者）情報
                  </Typography>
                </Box>
                {user && <Chip label="Googleアカウント情報適用中" size="small" color="primary" variant="outlined" />}
              </Box>

              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    size="small"
                    label="お名前"
                    value={shipperForm.Name}
                    onChange={(e) => setShipperForm({ ...shipperForm, Name: e.target.value })}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    size="small"
                    label="フリガナ"
                    value={shipperForm.Furigana}
                    onChange={(e) => setShipperForm({ ...shipperForm, Furigana: e.target.value })}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    size="small"
                    label="電話番号"
                    value={shipperForm.Phone}
                    onChange={(e) => setShipperForm({ ...shipperForm, Phone: e.target.value })}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <TextField
                      fullWidth
                      size="small"
                      label="郵便番号"
                      value={shipperForm.PostalCD}
                      onChange={(e) => setShipperForm({ ...shipperForm, PostalCD: e.target.value })}
                    />
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() => handleZipSearch(shipperForm.PostalCD, false)}
                      disabled={searchLoading}
                      sx={{ whiteSpace: 'nowrap' }}
                    >
                      住所補完
                    </Button>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    size="small"
                    label="都道府県"
                    value={shipperForm.PrefectureName}
                    onChange={(e) => setShipperForm({ ...shipperForm, PrefectureName: e.target.value })}
                  />
                </Grid>
                <Grid item xs={12} sm={8}>
                  <TextField
                    fullWidth
                    size="small"
                    label="市区町村・番地"
                    value={`${shipperForm.CityName}${shipperForm.Address1}`}
                    onChange={(e) => setShipperForm({ ...shipperForm, CityName: e.target.value })}
                  />
                </Grid>
              </Grid>
            </Paper>

            {/* STEP 3: お届け先指定（自分に送る vs 異なる住所に送る） */}
            <Paper elevation={0} sx={{ p: 3, mb: 3, borderRadius: 3, border: '1px solid #e2e8f0', bgcolor: '#ffffff' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <ShippingIcon color="primary" />
                <Typography variant="h6" fontWeight={700}>
                  3. お届け先（配送先）の指定
                </Typography>
              </Box>

              {/* 配送先モード切替チェックボックス */}
              <Box sx={{ bgcolor: '#f8fafc', p: 2, borderRadius: 2, mb: 3, border: '1px solid #cbd5e1' }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={isSendToSelf}
                      onChange={(e) => setIsSendToSelf(e.target.checked)}
                      color="primary"
                    />
                  }
                  label={
                    <Typography fontWeight={700} color="#1e293b">
                      既定で自分に送る（上記のご依頼主住所にお届け）
                    </Typography>
                  }
                />
                {!isSendToSelf && (
                  <Typography variant="caption" color="primary" display="block" sx={{ ml: 4, fontWeight: 600 }}>
                    ✓ 「異なる住所に送る」モードが有効になりました。以下でお届け先を指定してください。
                  </Typography>
                )}
              </Box>

              {/* 異なる住所に送る場合の入力エリア */}
              {!isSendToSelf && (
                <Box sx={{ pl: 1 }}>
                  <FormControl component="fieldset" sx={{ mb: 2 }}>
                    <FormLabel component="legend" sx={{ fontWeight: 600, fontSize: '0.875rem' }}>
                      お届け先住所の入力方式選択
                    </FormLabel>
                    <RadioGroup
                      row
                      value={differentAddressMode}
                      onChange={(e) => setDifferentAddressMode(e.target.value as 'saved' | 'new')}
                    >
                      <FormControlLabel value="new" control={<Radio size="small" />} label="新しい住所を入力する" />
                      {savedAddresses.length > 0 && (
                        <FormControlLabel value="saved" control={<Radio size="small" />} label="マイ配送先リストから選択" />
                      )}
                    </RadioGroup>
                  </FormControl>

                  {/* 登録済みマイ配送先からの選択 */}
                  {differentAddressMode === 'saved' && savedAddresses.length > 0 && (
                    <Box sx={{ mb: 3 }}>
                      <FormControl fullWidth size="small">
                        <InputLabel>保存済みお届け先</InputLabel>
                        <Select
                          value={selectedSavedConsigneeId}
                          label="保存済みお届け先"
                          onChange={(e) => setSelectedSavedConsigneeId(Number(e.target.value))}
                        >
                          {savedAddresses.map((addr) => (
                            <MenuItem key={addr.UserConsigneeId} value={addr.UserConsigneeId}>
                              {addr.Title} — {addr.Consignee?.Address?.Name} 様 ({addr.Consignee?.Address?.PrefectureName}
                              {addr.Consignee?.Address?.CityName})
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Box>
                  )}

                  {/* 新しいお届け先入力フォーム */}
                  {differentAddressMode === 'new' && (
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          size="small"
                          label="お届け先 お名前"
                          value={consigneeForm.Name}
                          onChange={(e) => setConsigneeForm({ ...consigneeForm, Name: e.target.value })}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          size="small"
                          label="フリガナ"
                          value={consigneeForm.Furigana}
                          onChange={(e) => setConsigneeForm({ ...consigneeForm, Furigana: e.target.value })}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          size="small"
                          label="お届け先 電話番号"
                          value={consigneeForm.Phone}
                          onChange={(e) => setConsigneeForm({ ...consigneeForm, Phone: e.target.value })}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <TextField
                            fullWidth
                            size="small"
                            label="郵便番号"
                            value={consigneeForm.PostalCD}
                            onChange={(e) => setConsigneeForm({ ...consigneeForm, PostalCD: e.target.value })}
                          />
                          <Button
                            variant="outlined"
                            size="small"
                            onClick={() => handleZipSearch(consigneeForm.PostalCD, true)}
                            disabled={searchLoading}
                            sx={{ whiteSpace: 'nowrap' }}
                          >
                            住所自動入力
                          </Button>
                        </Box>
                      </Grid>
                      {postalNotice && (
                        <Grid item xs={12}>
                          <Alert severity="success" onClose={() => setPostalNotice('')}>
                            {postalNotice}
                          </Alert>
                        </Grid>
                      )}
                      <Grid item xs={12} sm={4}>
                        <TextField
                          fullWidth
                          size="small"
                          label="都道府県"
                          value={consigneeForm.PrefectureName}
                          onChange={(e) => setConsigneeForm({ ...consigneeForm, PrefectureName: e.target.value })}
                        />
                      </Grid>
                      <Grid item xs={12} sm={8}>
                        <TextField
                          fullWidth
                          size="small"
                          label="市区町村"
                          value={consigneeForm.CityName}
                          onChange={(e) => setConsigneeForm({ ...consigneeForm, CityName: e.target.value })}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          size="small"
                          label="町名・番地"
                          value={consigneeForm.Address1}
                          onChange={(e) => setConsigneeForm({ ...consigneeForm, Address1: e.target.value })}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          size="small"
                          label="建物名・部屋番号"
                          value={consigneeForm.Address2}
                          onChange={(e) => setConsigneeForm({ ...consigneeForm, Address2: e.target.value })}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={consigneeForm.SaveToAddressBook}
                              onChange={(e) => setConsigneeForm({ ...consigneeForm, SaveToAddressBook: e.target.checked })}
                              color="primary"
                            />
                          }
                          label={
                            <Typography variant="body2" color="text.secondary">
                              <BookmarkIcon fontSize="small" sx={{ verticalAlign: 'middle', mr: 0.5, color: '#2563eb' }} />
                              このお届け先を「マイ配送先リスト」に保存し、次回使えるようにする
                            </Typography>
                          }
                        />
                      </Grid>
                    </Grid>
                  )}
                </Box>
              )}
            </Paper>
          </Grid>

          {/* 右カラム: 注文明細 & 送料・Stripe決済カード */}
          <Grid item xs={12} md={5}>
            <Paper
              elevation={2}
              sx={{
                p: 3,
                borderRadius: 3,
                position: 'sticky',
                top: 90,
                border: '1px solid #cbd5e1',
                bgcolor: '#ffffff',
              }}
            >
              <Typography variant="h6" fontWeight={700} gutterBottom sx={{ borderBottom: '2px solid #2563eb', pb: 1 }}>
                お支払い・ご注文合計
              </Typography>

              {/* 注文明細内訳 */}
              <Box sx={{ my: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2" color="text.secondary">
                    {selectedProduct.ProductName} × {quantity}
                  </Typography>
                  <Typography variant="body2" fontWeight={600}>
                    ¥{itemSubtotal.toLocaleString()}
                  </Typography>
                </Box>

                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2" color="text.secondary">
                    消費税 (10%)
                  </Typography>
                  <Typography variant="body2">¥{taxAmount.toLocaleString()}</Typography>
                </Box>

                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Typography variant="body2" color="text.secondary">
                      地域別配送料 ({targetPrefecture} / {totalWeightKg}kg)
                    </Typography>
                    <Tooltip title="お届け先の都道府県および商品の重量ランク（3kg, 5kg, 10kg, 20kg等）を元に自動計算されます">
                      <InfoIcon fontSize="small" color="action" />
                    </Tooltip>
                  </Box>
                  <Typography variant="body2" fontWeight={600} color="primary.main">
                    ¥{shippingFee.toLocaleString()}
                  </Typography>
                </Box>

                <Divider sx={{ my: 1.5 }} />

                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', mb: 2 }}>
                  <Typography variant="subtitle1" fontWeight={700}>
                    総合計（税込・送料込）
                  </Typography>
                  <Typography variant="h4" fontWeight={800} color="#2563eb">
                    ¥{grandTotal.toLocaleString()}
                  </Typography>
                </Box>
              </Box>

              {/* Stripe オーソリ・与信確保についての重要説明バナー */}
              <Alert severity="warning" icon={<StripeIcon fontSize="inherit" />} sx={{ mb: 2.5, borderRadius: 2 }}>
                <Typography variant="caption" fontWeight={700} display="block" gutterBottom>
                  💳 安心のカード与信（仮売上）方式
                </Typography>
                <Typography variant="caption" display="block">
                  ボタンを押すとカードの<strong>与信枠（仮売上）確保のみ</strong>を行います。<br />
                  実際のお引き落とし（本決済）は、<strong>商品出荷・配達完了時</strong>に実行されます。
                </Typography>
              </Alert>

              {/* 法定規約・特商法同意チェックボックス */}
              <Box sx={{ mb: 2.5 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={agreedTerms}
                      onChange={(e) => setAgreedTerms(e.target.checked)}
                      color="primary"
                    />
                  }
                  label={
                    <Typography variant="caption" color="text.secondary">
                      <Button
                        size="small"
                        onClick={() => navigate('/terms')}
                        sx={{ p: 0, minWidth: 0, fontSize: '0.75rem', textDecoration: 'underline' }}
                      >
                        利用規約
                      </Button>
                      、
                      <Button
                        size="small"
                        onClick={() => navigate('/terms')}
                        sx={{ p: 0, minWidth: 0, fontSize: '0.75rem', textDecoration: 'underline' }}
                      >
                        プライバシーポリシー
                      </Button>
                      および
                      <Button
                        size="small"
                        onClick={() => navigate('/tokushohon')}
                        sx={{ p: 0, minWidth: 0, fontSize: '0.75rem', textDecoration: 'underline' }}
                      >
                        特定商取引法に基づく表記
                      </Button>
                      に同意します。
                    </Typography>
                  }
                />
              </Box>

              {/* 決済・注文確定ボタン */}
              <Button
                fullWidth
                variant="contained"
                size="large"
                disabled={!agreedTerms || isSubmitting}
                onClick={handleSubmitOrder}
                startIcon={<StripeIcon />}
                sx={{
                  py: 1.5,
                  borderRadius: 2.5,
                  bgcolor: '#2563eb',
                  fontWeight: 700,
                  fontSize: '1rem',
                  textTransform: 'none',
                  boxShadow: '0 4px 12px rgba(37,99,235,0.25)',
                  '&:hover': { bgcolor: '#1d4ed8' },
                }}
              >
                {isSubmitting ? 'Stripe与信手続き中...' : 'Stripe決済画面へ進む（与信枠を確保）'}
              </Button>
            </Paper>
          </Grid>
        </Grid>
      </Box>
    </CustomerLayout>
  )
}
export default CheckoutPage
